import type { IPersistedAreaData } from '@gem-sdk/heatmap-react';

import { useHeatmapAreaClickContext, useHeatmapDataContext } from '@gem-sdk/heatmap-react';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { BlockStack, Box, Text } from '@shopify/polaris';
import { ClickAreaItem } from './ClickAreaItem';
import { useTranslation } from 'react-i18next';
import { SPACING_INSET } from '../Sidebar';

const CLICK_AREAS: IPersistedAreaData[] = [
  {
    kind: 'area',
    id: '5ogc7wjr4_1766392907956',
    hash: '5ogc7wjr4',
    selector: 'Browse our latest product',
  },
  {
    kind: 'area',
    id: 'lr72ozws_1766392913265',
    hash: 'lr72ozws',
    selector: 'Gift card that shows text',
  },
  {
    kind: 'area',
    id: 'lr72ozwr_1766393003213',
    hash: 'lr72ozwr',
    selector: 'A bar of golden yellow wa',
  },
  {
    kind: 'area',
    id: 'lr72ozwu_1766393003597',
    hash: 'lr72ozwu',
    selector: 'img.product-media__image',
  },
  {
    kind: 'area',
    id: 'lr72ozwt_1766393003973',
    hash: 'lr72ozwt',
    selector: 'img.product-media__image',
  },
  {
    kind: 'area',
    id: 'lr72ozwo_1766393004830',
    hash: 'lr72ozwo',
    selector: 'Top and bottom view of a ',
  },
  {
    kind: 'area',
    id: 'lr72ozwn_1766393005137',
    hash: 'lr72ozwn',
    selector: 'Top and bottom view of a ',
  },
  {
    kind: 'area',
    id: 'lr72ozwk_1766393005495',
    hash: 'lr72ozwk',
    selector: 'Top and bottom view of a ',
  },
  {
    kind: 'area',
    id: 'lr72ozwj_1766393005931',
    hash: 'lr72ozwj',
    selector: 'Top and bottom view of a ',
  },
];

interface ClickAreaProps {
  isScrolled: boolean;
}

export const ClickArea: React.FC<ClickAreaProps> = ({ isScrolled }) => {
  const areas = useHeatmapAreaClickContext((s) => s.areas);
  const setClickAreas = useHeatmapDataContext((s) => s.setClickAreas);

  const { t } = useTranslation();

  const areaRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const sortedAreas = useMemo(() => [...areas].sort((a, b) => b.clickDist - a.clickDist), [areas]);

  const setAreaRef = useCallback((areaId: string) => {
    return (node: HTMLDivElement | null) => {
      if (node) {
        areaRefs.current.set(areaId, node);
      } else {
        areaRefs.current.delete(areaId);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof areas === 'undefined') return;

    setClickAreas(CLICK_AREAS);
  }, [areas, setClickAreas]);

  return (
    <>
      <AutoScrollHandler areaRefs={areaRefs} />
      {isScrolled && (
        <div
          className="gx-hm-divider"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            width: '100%',
          }}
        />
      )}
      <Box padding={SPACING_INSET} paddingBlockStart="025">
        {sortedAreas.length === 0 ? (
          <Box padding="400">
            <BlockStack gap="200" align="center">
              <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
                {t('No areas created yet')}
              </Text>
              <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                {t('Enter edit mode to create areas by clicking on elements')}
              </Text>
            </BlockStack>
          </Box>
        ) : (
          <BlockStack gap={SPACING_INSET}>
            {sortedAreas.map((area, index) => (
              <ClickAreaItem key={area.id} ref={setAreaRef(area.id)} rank={index + 1} area={area} />
            ))}
          </BlockStack>
        )}
      </Box>
    </>
  );
};

const AutoScrollHandler: React.FC<{ areaRefs: React.RefObject<Map<string, HTMLDivElement>> }> =
  memo(({ areaRefs }) => {
    const selectedArea = useHeatmapAreaClickContext((s) => s.selectedArea);

    useEffect(() => {
      if (selectedArea) {
        const areaNode = areaRefs.current?.get(selectedArea.id);
        if (areaNode) {
          areaNode.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }
    }, [areaRefs, selectedArea]);
    return null;
  });
